import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Briefcase, CalendarCheck, FileText, Receipt, ShieldCheck, UsersThree,
} from "@phosphor-icons/react";
import MarketingLayout, { PageMeta } from "@/components/MarketingLayout";

const features = [
  {
    icon: Briefcase,
    title: "Matters stay complete",
    body: "Keep parties, advocates, hearings, notes and documents connected to the right case.",
    className: "md:col-span-7 marketing-feature-primary",
  },
  {
    icon: CalendarCheck,
    title: "Every date stays visible",
    body: "Plan hearings, tasks, reminders and daily boards without rebuilding tomorrow from memory.",
    className: "md:col-span-5",
  },
  {
    icon: Receipt,
    title: "Fees stay accountable",
    body: "Prepare bills, receipts, quotations and statements with a clear ledger behind them.",
    className: "md:col-span-5",
  },
  {
    icon: FileText,
    title: "Documents stay close",
    body: "Store, version and bundle the working papers that move a matter forward.",
    className: "md:col-span-7 marketing-feature-image",
  },
];

export default function Home() {
  return (
    <MarketingLayout>
      <PageMeta
        title="Nothing Before Law | Case Diary for Indian Advocates"
        description="Manage matters, hearings, clients, documents and billing in one focused case diary for Indian advocates."
      />

      <section className="mx-auto grid min-h-[calc(100dvh-72px)] max-w-7xl items-center gap-10 px-4 py-10 sm:px-6 md:grid-cols-[0.88fr_1.12fr] lg:gap-14 lg:px-8 lg:py-12">
        <div className="marketing-hero-copy max-w-xl">
          <p className="marketing-eyebrow">Built for Indian legal practice</p>
          <h1 className="mt-5 font-serif text-5xl leading-[1.08] tracking-[-0.025em] text-[var(--m-text)] sm:text-6xl lg:text-6xl">
            Your practice, ready for court.
          </h1>
          <p className="mt-6 max-w-[34rem] text-lg leading-7 text-[var(--m-muted)]">
            Cases, hearings, clients and billing in one secure diary built for Indian advocates.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/login?mode=register" className="marketing-primary-button">
              Create account <ArrowRight size={17} weight="bold" />
            </Link>
            <Link to="/login" className="marketing-secondary-button">Sign in</Link>
          </div>
        </div>
        <div className="marketing-hero-image-wrap">
          <img
            src="/images/marketing/advocates-courthouse-hero.webp"
            alt="Two Indian advocates reviewing a case diary in a courthouse corridor"
            width="1536"
            height="1024"
            fetchPriority="high"
            className="h-full w-full object-cover object-[66%_center]"
          />
        </div>
      </section>

      <section className="marketing-section mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <h2 className="font-serif text-4xl leading-tight text-[var(--m-text)] sm:text-5xl">A court day, held together.</h2>
          <p className="mt-4 text-lg leading-7 text-[var(--m-muted)]">
            The diary connects daily work so every matter has context before you step into court.
          </p>
        </div>
        <div className="mt-10 grid gap-4 md:grid-cols-12">
          {features.map(({ icon: Icon, title, body, className }) => (
            <article
              key={title}
              className={`marketing-feature ${className}`}
              style={className.includes("marketing-feature-image") ? {
                backgroundImage: 'linear-gradient(100deg, rgb(18 26 47 / 0.92), rgb(18 26 47 / 0.48)), url("/images/marketing/advocate-chambers.webp")',
              } : undefined}
            >
              <Icon size={28} weight="duotone" aria-hidden="true" />
              <h3 className="mt-8 font-serif text-2xl text-[var(--m-text)]">{title}</h3>
              <p className="mt-3 max-w-lg text-sm leading-6 text-[var(--m-muted)]">{body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="marketing-section mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 md:grid-cols-[1.1fr_0.9fr] lg:gap-16 lg:px-8">
        <div className="marketing-image-frame order-2 md:order-1">
          <img
            src="/images/marketing/advocates-entering-court.webp"
            alt="Indian advocates carrying case files toward a district court"
            width="1448"
            height="1086"
            loading="lazy"
            className="aspect-[4/3] w-full object-cover"
          />
        </div>
        <div className="order-1 md:order-2">
          <h2 className="font-serif text-4xl leading-tight text-[var(--m-text)] sm:text-5xl">Made for practice, not paperwork.</h2>
          <p className="mt-5 text-lg leading-7 text-[var(--m-muted)]">
            Nothing Before Law follows the way advocates already work, then removes the repetition that slows a chamber down.
          </p>
          <Link to="/about" className="marketing-text-link mt-7 inline-flex items-center gap-2">
            Why we built it <ArrowRight size={17} weight="bold" />
          </Link>
        </div>
      </section>

      <section className="marketing-section marketing-tinted-section">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 md:grid-cols-[0.9fr_1.1fr] lg:gap-16 lg:px-8">
          <div>
            <h2 className="font-serif text-4xl leading-tight text-[var(--m-text)] sm:text-5xl">One calm view of tomorrow.</h2>
            <p className="mt-5 text-lg leading-7 text-[var(--m-muted)]">
              Prepare the next hearing, update the client and account for the fee from the same trusted record.
            </p>
            <div className="mt-8 grid gap-5">
              {[
                [CalendarCheck, "Prepare", "See hearings, deadlines and tasks before the day begins."],
                [UsersThree, "Coordinate", "Keep clients, colleagues and matter ownership clear."],
                [ShieldCheck, "Protect", "Use account sessions and a separate six-digit PIN lock."],
              ].map(([Icon, title, body]) => (
                <div key={title} className="flex gap-4">
                  <span className="marketing-small-icon"><Icon size={21} weight="duotone" /></span>
                  <div>
                    <h3 className="font-semibold text-[var(--m-text)]">{title}</h3>
                    <p className="mt-1 text-sm leading-6 text-[var(--m-muted)]">{body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="marketing-image-frame">
            <img
              src="/images/marketing/advocate-chambers.webp"
              alt="An Indian advocate organising the next court day in her chambers"
              width="1448"
              height="1086"
              loading="lazy"
              className="aspect-[4/3] w-full object-cover"
            />
          </div>
        </div>
      </section>

      <section className="marketing-section px-4 sm:px-6 lg:px-8">
        <div className="marketing-cta mx-auto max-w-5xl text-center">
          <h2 className="font-serif text-4xl leading-tight text-[var(--m-text)] sm:text-5xl">Give every matter a proper place.</h2>
          <p className="mx-auto mt-4 max-w-xl text-lg leading-7 text-[var(--m-muted)]">
            Start a case diary that stays useful from first conference to final receipt.
          </p>
          <Link to="/login?mode=register" className="marketing-primary-button mt-8">Create account</Link>
        </div>
      </section>
    </MarketingLayout>
  );
}
